// Tremor Radio Card [v0.0.3]
import * as RadioGroupPrimitives from "@radix-ui/react-radio-group";

import React from "react";
import { FaCheck } from "react-icons/fa";

import { cx, focusInput, focusRing } from "@/lib/tailwind-utils";

const RadioCardGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitives.Root>,
  Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitives.Root>,
    "onValueChange" | "value"
  > & {
    required?: boolean;
    onValueChange?: (value: string | undefined) => void;
    value?: string | undefined;
    disabled?: boolean;
  }
>(
  (
    { className, required = true, value, onValueChange, disabled, ...props },
    forwardedRef,
  ) => {
    const handleValueChange = (newValue: string) => {
      if (!required && newValue === value) {
        onValueChange?.(undefined);
        return;
      }
      onValueChange?.(newValue);
    };

    return (
      <RadioGroupPrimitives.Root
        disabled={disabled}
        ref={forwardedRef}
        className={cx("grid gap-2", className)}
        required={required}
        value={!disabled && value ? value : ""}
        onValueChange={handleValueChange}
        {...props}
      />
    );
  },
);

RadioCardGroup.displayName = "RadioCardGroup";

interface RadioCardItemProps extends React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitives.Item
> {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}

const RadioCardItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitives.Item>,
  RadioCardItemProps
>(
  (
    { className, children, title, description, icon, ...props },
    forwardedRef,
  ) => {
    return (
      <RadioGroupPrimitives.Item
        ref={forwardedRef}
        className={cx(
          // base
          "group relative w-full cursor-pointer rounded-md border p-4 text-left shadow-sm transition focus:outline-none",
          // background color
          "bg-background",
          // border color
          "border-border hover:border-primary/50",
          "data-[state=checked]:border-primary data-[state=checked]:ring-2 data-[state=checked]:ring-primary/60",
          "data-[state=checked]:cursor-default",
          "data-[state=checked]:bg-accent/50",
          "data-[state=unchecked]:border-border",
          // disabled
          "data-[disabled]:border-border-secondary data-[disabled]:ring-0 data-[disabled]:hover:border-border-secondary",
          "data-[disabled]:bg-background-secondary data-[disabled]:shadow-none",
          "data-[disabled]:cursor-not-allowed",
          focusInput,
          className,
        )}
        {...props}
      >
        <div className="flex flex-col items-center">
          {icon && <div className="mb-2 text-4xl">{icon}</div>}
          {title && (
            <h3 className="text-center text-h3 font-medium">{title}</h3>
          )}
          {description && (
            <p className="mt-2 text-center text-body text-muted-foreground">
              {description}
            </p>
          )}
          {children}
        </div>
        <RadioCardCheckedIndicator />
      </RadioGroupPrimitives.Item>
    );
  },
);

RadioCardItem.displayName = "RadioCardItem";

const RadioCardCheckedIndicator = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitives.Indicator>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitives.Indicator>
>(({ className, ...props }, forwardedRef) => {
  return (
    <RadioGroupPrimitives.Indicator
      ref={forwardedRef}
      className={cx("absolute right-4 top-4", className)}
      {...props}
    >
      <FaCheck
        className={cx(
          "h-4 w-4 shrink-0 text-primary",
          "group-data-[disabled]:invisible",
        )}
        aria-hidden="true"
      />
    </RadioGroupPrimitives.Indicator>
  );
});

RadioCardCheckedIndicator.displayName = "RadioCardCheckedIndicator";

const RadioCardIndicator = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitives.Indicator>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitives.Indicator>
>(({ className, ...props }, forwardedRef) => {
  return (
    <div
      className={cx(
        // base
        "relative flex size-4 shrink-0 appearance-none items-center justify-center rounded-full border shadow-sm outline-none",
        // border color

        // background color
        "",
        // checked
        "group-data-[state=checked]:border-0 group-data-[state=checked]:border-transparent group-data-[state=checked]:bg-primary",
        // disabled
        "group-data-[disabled]:border-border group-data-[disabled]:bg-muted group-data-[disabled]:text-foreground-muted",
        // focus
        focusRing,
        className,
      )}
    >
      <RadioGroupPrimitives.Indicator
        ref={forwardedRef}
        className={cx("flex items-center justify-center")}
        {...props}
      >
        <div
          className={cx(
            // base
            "size-1.5 shrink-0 rounded-full",
            // indicator
            "",
            // disabled
            "group-data-[disabled]:bg-foreground-muted",
          )}
        />
      </RadioGroupPrimitives.Indicator>
    </div>
  );
});

RadioCardIndicator.displayName = "RadioCardIndicator";

export {
  RadioCardCheckedIndicator,
  RadioCardGroup,
  RadioCardIndicator,
  RadioCardItem,
};
